import { FormEvent, useState } from "react";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Globe2,
  Mail,
  MessageCircle,
  ServerCog,
  ShieldCheck,
  Users,
} from "lucide-react";
import { Link } from "wouter";
import { EVALUATION_FAQS } from "./evaluationContent";
import { launchListEnabled, submitLaunchLead } from "./firebase";

const evaluationJourney = [
  [Globe2, "Set up a test domain"],
  [Users, "Create mailboxes and aliases"],
  [Mail, "Test everyday email workflows"],
  [MessageCircle, "Share candid feedback"],
] as const;

const evaluationFit = [
  [Users, "3–25 employees"],
  [Globe2, "1–3 business domains"],
  [Mail, "3–20 mailboxes"],
  [ServerCog, "Currently using cPanel, GoDaddy, Zoho or another basic host"],
] as const;

const evaluationSteps = [
  ["Set up", "Verify a secondary or test domain."],
  ["Configure", "Create mailboxes, aliases and authentication records."],
  ["Use", "Send, receive and try everyday administration."],
  ["Review", "Meet with Envelaro and share candid feedback."],
] as const;

const participantBenefits = [
  "Guided onboarding",
  "Help interpreting DNS and Email Health results",
  "A structured product-feedback session",
  "No requirement to provide a testimonial",
] as const;

export function EvaluationPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [startedAt] = useState(() => Date.now());

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!launchListEnabled) return;
    const form = event.currentTarget;
    const data = new FormData(form);
    setSubmitting(true);
    setError("");

    try {
      await submitLaunchLead({
        name: String(data.get("name") ?? ""),
        email: String(data.get("email") ?? ""),
        company: String(data.get("company") ?? ""),
        teamSize: String(data.get("teamSize") ?? ""),
        currentProvider: String(data.get("currentProvider") ?? ""),
        message: `[Evaluation request]\n${String(data.get("message") ?? "")}`,
        consent: true,
        privacyVersion: "website-privacy-2026-07-28",
        source: "website_contact",
        leadType: "general",
        website: String(data.get("website") ?? ""),
        elapsedMs: Date.now() - startedAt,
      });
      form.reset();
      setSubmitted(true);
    } catch (nextError) {
      setError(
        nextError instanceof Error
          ? nextError.message
          : "We could not save your evaluation request. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="evaluation-page">
      <section className="evaluation-hero">
        <div className="container evaluation-hero__grid">
          <div className="evaluation-hero__copy">
            <h1>Help shape a better managed business email service.</h1>
            <p>
              We are inviting a small group of companies to evaluate Envelaro's
              domain setup, administration, webmail, security guidance and
              migration planning—with guided support from our team.
            </p>
            <div className="button-row">
              <a className="button button--primary" href="#apply">
                Request an evaluation <ArrowRight size={18} />
              </a>
              <a className="button button--secondary" href="#evaluation-steps">
                See what you will test
              </a>
            </div>
          </div>
          <div className="evaluation-journey" aria-label="Evaluation journey">
            {evaluationJourney.map(([Icon, label]) => (
              <div key={label}>
                <Icon aria-hidden="true" />
                <span>{label}</span>
                <i aria-hidden="true" />
              </div>
            ))}
            <p><ShieldCheck aria-hidden="true" />No primary email cutover required.</p>
          </div>
        </div>
      </section>

      <section className="evaluation-fit">
        <div className="container evaluation-fit__grid">
          <div>
            <h2>A practical evaluation for small teams.</h2>
            <p>
              Best suited to companies with 3–25 employees, one to three
              business domains and a decision-maker who can join onboarding and
              provide candid feedback.
            </p>
            <p>
              During the evaluation, we want to learn whether domain guidance is
              clear, whether administrators can understand mailbox and alias
              controls, and whether everyday webmail workflows feel dependable
              for a working team. Practical observations are more useful than a
              general approval or a polished testimonial.
            </p>
            <p>
              Your team can focus on the areas that matter to its current setup:
              DNS preparation, mailbox administration, sending and receiving,
              authentication guidance, support, or migration planning. You can
              stop before any production routing change if Envelaro is not the
              right operational fit.
            </p>
          </div>
          <div className="evaluation-fit__list">
            {evaluationFit.map(([Icon, label]) => (
              <div key={label}><Icon aria-hidden="true" /><span>{label}</span></div>
            ))}
          </div>
        </div>
      </section>

      <section className="evaluation-steps" id="evaluation-steps">
        <div className="container">
          <h2>Test the work that matters before any cutover.</h2>
          <div className="evaluation-steps__rail">
            {evaluationSteps.map(([title, copy], index) => (
              <article key={title}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <h3>{title}</h3>
                <p>{copy}</p>
              </article>
            ))}
          </div>
          <p className="evaluation-steps__note">
            <ShieldCheck aria-hidden="true" />
            Use a secondary or non-critical domain first. Moving primary email is not required.
          </p>
        </div>
      </section>

      <section className="evaluation-apply" id="apply">
        <div className="container evaluation-apply__grid">
          <div className="evaluation-benefits">
            <h2>What participants receive.</h2>
            <ul>
              {participantBenefits.map((benefit) => (
                <li key={benefit}><Check aria-hidden="true" />{benefit}</li>
              ))}
            </ul>
            <p>This is an evaluation of product fit—not a request for a favorable review.</p>
          </div>

          {submitted ? (
            <div className="evaluation-success" role="status">
              <CheckCircle2 aria-hidden="true" />
              <div>
                <h2>Thanks. We received your evaluation request.</h2>
                <p>We will review the details and get in touch.</p>
              </div>
            </div>
          ) : (
            <form className="evaluation-form" onSubmit={handleSubmit}>
              <h2>Request an evaluation</h2>
              <div className="evaluation-form__fields">
                <label>Name<input autoComplete="name" name="name" required minLength={2} /></label>
                <label>Work email<input autoComplete="email" name="email" required type="email" /></label>
                <label>Company<input autoComplete="organization" name="company" required maxLength={140} /></label>
                <label>Team size
                  <select defaultValue="" name="teamSize" required>
                    <option disabled value="">Select team size</option>
                    <option value="1-5">1–5</option>
                    <option value="6-20">6–20</option>
                    <option value="21-50">21–50</option>
                    <option value="51-200">51–200</option>
                    <option value="200+">More than 200</option>
                  </select>
                </label>
                <label className="evaluation-form__wide">Current email provider
                  <input name="currentProvider" maxLength={120} placeholder="For example, cPanel or Zoho" />
                </label>
                <label className="evaluation-form__wide">What would you like to evaluate?
                  <textarea name="message" required minLength={10} maxLength={2979} rows={4} placeholder="Tell us about your domains, mailboxes and the workflows you want to test." />
                </label>
                <label className="contact-form__honeypot" aria-hidden="true">
                  Website<input name="website" tabIndex={-1} autoComplete="off" />
                </label>
                <label className="evaluation-form__consent evaluation-form__wide">
                  <input name="consent" type="checkbox" required />
                  <span>I agree that Envelaro may store these details and contact me about my evaluation request. See our <Link href="/privacy">privacy notice</Link>.</span>
                </label>
              </div>
              {error ? <p className="evaluation-form__error" role="alert">{error}</p> : null}
              <button className="button button--primary" type="submit" disabled={!launchListEnabled || submitting}>
                {submitting ? "Submitting..." : "Submit evaluation request"}
                {!submitting ? <ArrowRight size={18} /> : null}
              </button>
              {!launchListEnabled ? <p className="evaluation-form__error">Online evaluation requests are temporarily unavailable.</p> : null}
            </form>
          )}
        </div>
      </section>

      <section className="evaluation-faq">
        <div className="container">
          <h2>Questions about the evaluation</h2>
          <div className="evaluation-faq__grid">
            {EVALUATION_FAQS.map(({ question, answer }) => (
              <details key={question}>
                <summary>{question}<span aria-hidden="true">+</span></summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
